import { Lock, TrendingUp, Zap } from "lucide-react";

/**
 * The world inside a private plan (PRD §14, Loop 4), and "What moved" with its reasons (D12). The diagram is drawn
 * by hand; its numbers are illustrative.
 */
export function World() {
	return (
		<section id="world" className="border-y border-line bg-[#fbf9f9]">
			<div className="mx-auto grid max-w-[1240px] items-center gap-x-16 gap-y-14 px-5 py-24 md:px-8 lg:grid-cols-12 lg:py-32">
				<div className="lg:order-2 lg:col-span-5 lg:col-start-8">
					<h2 className="text-[clamp(2.25rem,4.4vw,3.5rem)] leading-[1.02] tracking-[-0.035em]">The world, inside your plan.</h2>
					<p className="mt-6">
						A plan that ignores the economy is a plan for some other economy. Put a recession, a market crash or a rule change in your worldview beside your
						clients and costs, link them, and a change in the odds reaches your headcount with its reasons attached.
					</p>
					<p className="mt-4">
						Each move in a headline number says what moved it. That is the difference between a number that changed and a number you can explain to a board.
					</p>
					<p className="mt-6 flex gap-2.5 text-sm text-quiet">
						<Lock size={16} className="mt-[3px] shrink-0" aria-hidden />
						Private nodes stay private. Client names and revenue never leave the workspace.
					</p>
				</div>
				<div className="lg:order-1 lg:col-span-7">
					<Diagram />
				</div>
			</div>
		</section>
	);
}

function Node({ icon, name, value, sub, className }: { icon: "event" | "metric"; name: string; value: string; sub: string; className?: string }) {
	const Icon = icon === "event" ? Zap : TrendingUp;
	return (
		<div
			className={`rounded-sm border border-line border-l-[3px] border-l-peri bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgb(20_20_28/0.05)] ${className ?? ""}`}
		>
			<div className="flex items-center gap-1.5 text-[13px] font-medium text-ink">
				<Icon size={13} className="shrink-0 text-quiet" aria-hidden />
				{name}
			</div>
			<div className="tnum mt-1 text-lg leading-tight font-semibold text-ink">{value}</div>
			<div className="text-[11px] text-quiet">{sub}</div>
		</div>
	);
}

function Diagram() {
	return (
		<figure
			className="relative"
			aria-label="World events feed a private plan: US recession and AI equity crash link to client events, which drive Layoffs needed."
		>
			<div className="relative rounded-md border border-dashed border-line bg-paper/60 p-5 sm:p-7">
				<div className="mb-4 text-xs text-quiet">The world</div>
				<div className="grid grid-cols-2 gap-4 sm:gap-6">
					<Node icon="event" name="US recession" value="38%" sub="by end of 2027, up from 30%" />
					<Node icon="event" name="AI equity crash" value="17%" sub="by end of 2028" />
				</div>

				<div className="relative my-5 h-16" aria-hidden>
					<svg className="absolute inset-0 h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 100 64">
						<path d="M25 0 C25 30, 25 34, 25 64" stroke="var(--brand-periwinkle)" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" />
						<path
							d="M75 0 C75 30, 75 34, 75 64"
							stroke="var(--brand-periwinkle)"
							strokeWidth="1.5"
							fill="none"
							vectorEffect="non-scaling-stroke"
							strokeDasharray="4 3"
						/>
					</svg>
					<span className="voice absolute top-1/2 left-[27%] max-w-[44%] -translate-y-1/2 text-[13px] leading-tight text-eggplant sm:text-[15px]">
						While it lasts, Acme leaving is 2× as likely
					</span>
					<span className="voice absolute top-1/2 left-[77%] hidden -translate-y-1/2 text-[13px] leading-tight text-eggplant sm:block sm:text-[15px]">
						30% chance Globex
						<br />
						halves spend
					</span>
				</div>

				<div className="rounded-sm border border-line bg-white/70 p-4 sm:p-5">
					<div className="mb-3 flex items-center gap-1.5 text-xs text-quiet">
						<Lock size={12} aria-hidden /> Your workspace
					</div>
					<div className="grid grid-cols-2 gap-4 sm:gap-6">
						<Node icon="event" name="Acme leaves" value="31%" sub="by end of 2027" />
						<Node icon="event" name="Globex halves spend" value="11%" sub="by end of 2027" />
					</div>
					<div className="mt-4 flex justify-center">
						<Node icon="event" name="Layoffs needed" value="23%" sub="by Dec 2027" className="w-[min(100%,15rem)] border-l-scenario" />
					</div>
				</div>
			</div>

			{/* "What moved", as the Worldview page shows it. */}
			<div className="relative z-10 mx-auto -mt-8 w-[min(92%,23rem)] rounded-md border border-line bg-white p-4 shadow-[0_18px_40px_-18px_rgb(20_20_28/0.35)]">
				<div className="text-xs text-quiet">What moved this week</div>
				<div className="mt-1.5 flex items-baseline gap-2">
					<span className="text-sm font-medium text-ink">Layoffs needed</span>
					<span className="tnum ml-auto text-sm text-quiet">
						18% <span aria-hidden>→</span>
						<span className="sr-only">to</span> <span className="font-semibold text-risk-ink">23%</span>
					</span>
				</div>
				<p className="mt-1.5 text-[13px] leading-snug text-eggplant">
					Because US recession rose from 30% to 38% by end of 2027, and Acme leaving is linked to it.
				</p>
			</div>
		</figure>
	);
}
